import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import {
  SEND_HOLD_LINGER_MS,
  sendHoldDisplay,
  sendHoldDisplayLine,
  sendHoldNextWake,
  sendHoldShowsTime,
  sendHoldUi,
  sendHoldVisible,
  sendHoldChannelLabel,
  sendNowFailedTime,
  type SendHoldBucket,
  type SendHoldCancelResponse,
  type SendHoldLiveState,
  type SendHoldPhase,
  type SendHoldSurface,
  type SendNowResponse,
} from '../../../lib/send-hold';
import { useHoldCallsOut, useHoldCancel, useOnScreen } from '../../../lib/use-held-sends';
import { Button, Card, Typography } from '../ui';
import styles from './HeldSendRow.module.css';

/** The owner's own zone, as the browser has it — the zone the send time is read in. */
function readerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** A hold's channel as a word (`sendHoldChannelLabel`): translated when known, the word title-cased otherwise (never blanked). */
export function useChannelLabel(): (channel: string | undefined) => string {
  const { t } = useTranslation();
  // A missing key comes back as the key itself: that is "no translation", so the word is title-cased instead.
  return (channel) =>
    sendHoldChannelLabel(channel, (key) => {
      const label = t(key);
      return label && label !== key ? label : undefined;
    });
}

/**
 * One message that is WAITING to go out (the send rule), with its Cancel — and, on a MEETING hold, Send now. Shared by the chat chip (surface `chat`),
 * the task screen's "Waiting to send" (`task`) and a scheduled thread's stage card (`thread`), so the timing, the
 * outcome lines and the Cancel cannot drift between them — and mirrored 1:1 by iOS/Android `HeldSendRow`.
 *
 * - The line under the title is the send time (over an hour), a m:ss countdown (within it) or "Sending…"; it is
 *   replaced by the outcome once there is one, and the row leaves 30s after the send time — it never says "Sent".
 * - The clock runs only while the row is on screen and stops on every exit (unmount, final phase, off screen).
 * - Cancel calls `/api/held-sends/<id>/cancel` directly: never a chat send, never gated on a streaming reply, a
 *   locked channel or credits. A failure keeps the button (`cancelFailed`), and so does a failed live read.
 * - Send now (only when `offersSendNow`: a HELD MEETING hold not already sent now) calls
 *   `/api/held-sends/<id>/send-now` ONCE — no retry, both buttons disabled while it is out. "Sending…" once accepted,
 *   "Sent" only when the answer is SENT, and "Couldn't send now. It will go at {time}." with both buttons back when it
 *   is still waiting. What each phase shows is the contract's `phases.ui` table (`sendHoldUi`).
 */
export function HeldSendRow({
  holdId,
  fireAt,
  bucket,
  title,
  preview,
  surface,
  live,
  canCancel = true,
  offersSendNow = false,
  bare = false,
  hideTime = false,
  onZero,
  onSettled,
  cancel,
  sendNow,
}: {
  holdId: string;
  fireAt: string;
  bucket?: SendHoldBucket;
  /** "To Dana · WhatsApp" — already rendered by the caller (its names come from different places per surface). */
  title?: string;
  preview?: string;
  surface: SendHoldSurface;
  live: SendHoldLiveState;
  canCancel?: boolean;
  /** The hold still offers Send now (`sendHoldLiveView` / `heldSendRowOffersSendNow`). Off by default. */
  offersSendNow?: boolean;
  /** Drawn without its own frame — inside a card that already has one (the stage card). */
  bare?: boolean;
  /** The surface already prints the send time (the stage card's own stage line): draw only Cancel and its outcome. */
  hideTime?: boolean;
  /** The countdown reached zero on screen: re-read the live state, once. */
  onZero?: () => void;
  onSettled?: (phase: SendHoldPhase) => void;
  /** Test seam for the Cancel call. */
  cancel?: (heldSendId: string, surface: SendHoldSurface) => Promise<SendHoldCancelResponse>;
  /** Test seam for the Send now call. */
  sendNow?: (heldSendId: string, surface: SendHoldSurface) => Promise<SendNowResponse | undefined>;
}) {
  const { t } = useTranslation();
  const heldDate = useMemo(() => new Date(fireAt), [fireAt]);
  const { phase, onCancel, onSendNow, sendNowAnswer } = useHoldCancel({
    holdId,
    fireAt: heldDate,
    bucket,
    surface,
    live,
    onSettled,
    ...(cancel ? { cancel } : {}),
    ...(sendNow ? { sendNow } : {}),
  });
  // Another row's call for this hold (M2): what it would make this row ignore reads disabled here too.
  const callsOut = useHoldCallsOut(holdId);
  // Once Send now is accepted the hold's time is now: the row reads "Sending…" and leaves 30s later.
  const fireDate = phase === 'sendNowSending' && sendNowAnswer?.fireAt ? sendNowAnswer.fireAt : heldDate;
  const [ref, onScreen] = useOnScreen<HTMLDivElement>();
  const [now, setNow] = useState(() => new Date());
  const zeroFired = useRef(false);
  const zone = useMemo(readerTimeZone, []);

  // One owned timer at a time: re-armed from `sendHoldNextWake`, stopped on unmount, off screen and final phases.
  useEffect(() => {
    if (onScreen) setNow(new Date());
  }, [onScreen]);
  useEffect(() => {
    if (!onScreen) return;
    const current = now;
    // With no time line on screen there is nothing to tick: wake once, at the hide.
    const hideIn = fireDate.getTime() + SEND_HOLD_LINGER_MS - current.getTime();
    const wait = hideTime ? ((sendHoldShowsTime(phase) || phase === 'sendNowSending') && hideIn > 0 ? hideIn : undefined) : sendHoldNextWake(fireDate, current, phase);
    if (wait === undefined) return;
    const timer = setTimeout(() => setNow(new Date()), Math.max(16, wait));
    return () => clearTimeout(timer);
  }, [onScreen, fireDate, phase, now, hideTime]);

  useEffect(() => {
    if (zeroFired.current || now.getTime() < fireDate.getTime() || !sendHoldVisible(fireDate, now, phase) || !sendHoldShowsTime(phase)) return;
    zeroFired.current = true;
    onZero?.();
  }, [now, fireDate, phase, onZero]);

  if (!sendHoldVisible(fireDate, now, phase)) return null;

  const view = sendHoldUi(phase);
  const timeLine = !hideTime && view.showsTime ? sendHoldDisplayLine(sendHoldDisplay(fireDate, now, zone)) : undefined;
  const outcomeKey = view.line ?? undefined;
  const failed = phase === 'cancelFailed' || phase === 'sendNowFailed';
  const outcomeParams = phase === 'sendNowFailed' ? { time: sendNowFailedTime(heldDate, sendNowAnswer?.sendAt, zone) } : undefined;
  const showCancel = canCancel && view.showsCancel;
  const showSendNow = offersSendNow && view.showsSendNow;

  const body = (
    <div className={styles.row}>
      <div className={styles.text}>
        {title ? (
          // The line follows the UI's direction; the name inside it is bidi-isolated by the caller (`isolateName`).
          <Typography variant="small-medium" color="primary">
            {title}
          </Typography>
        ) : null}
        {preview ? (
          <Typography variant="p3" color="secondary" dir="auto" className={styles.preview}>
            {preview}
          </Typography>
        ) : null}
        {outcomeKey ? (
          <Typography variant="p3" color={failed ? 'inherit' : 'secondary'} className={failed ? styles.failed : undefined} role={failed ? 'alert' : 'status'}>
            {t(outcomeKey, outcomeParams)}
          </Typography>
        ) : null}
        {/* Not a live region: a countdown that announced itself every second would drown everything else. */}
        {timeLine ? (
          <Typography variant="p3" color="secondary">
            {t(timeLine.key, timeLine.params)}
          </Typography>
        ) : null}
      </div>
      {showSendNow || showCancel ? (
        <div className={styles.actions}>
          {showSendNow ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => void onSendNow()}
              disabled={!view.sendNowEnabled || callsOut !== 'none'}
              aria-label={title ? `${t('sendHold.sendNow')} · ${title}` : undefined}
            >
              {t('sendHold.sendNow')}
            </Button>
          ) : null}
          {showCancel ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => void onCancel()}
              disabled={!view.cancelEnabled || callsOut === 'sendNow'}
              aria-label={title ? `${t('sendHold.cancel')} · ${title}` : undefined}
            >
              {phase === 'cancelling' ? t('sendHold.cancelling') : t('sendHold.cancel')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
  return (
    <div ref={ref} data-testid="held-send-row" data-hold-id={holdId}>
      {bare ? (
        body
      ) : (
        <Card variant="outline" size="sm">
          {body}
        </Card>
      )}
    </div>
  );
}
